import React from "react";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import BayHireAttachmentsSection from "@features/bay-hire/components/BayHireRequestDetail/BayHireAttachmentsSection";
import type { Attachment } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

interface DetailsDialogProps {
  open: boolean;
  /** "Rejection reason" or "Cancellation reason", matching whichever banner opened this. */
  reasonLabel: string;
  reason: string | null | undefined;
  /** DFT-0044 slice F (F3): the request's SUPPORTING_DOCUMENT attachment(s), if any. */
  attachments: Attachment[];
  isDownloading: (documentUrl: string) => boolean;
  /** Opens the attachment in a new tab (view) - AC29. */
  onView: (attachment: Attachment) => void;
  onDownload: (attachment: Attachment) => void;
  onClose: () => void;
}

/**
 * DFT-0044 slice F (F4): read-only `Details` drawer
 * (`05_Port_License_details` "Details" wireframe) - a read-only reason, the F3
 * supporting-document attachment(s) with download, and a single `OK` button.
 *
 * Deliberately unconditional (always shown when a reason exists, per the wireframe literally) -
 * unlike RejectDialog/CancelDialog's own optional upload, this drawer's presence does not depend
 * on whether an attachment was actually uploaded; it repeats the already-visible banner reason in
 * that case, matching what the wireframe itself shows (a single generic layout, no empty-state
 * variant drawn for "no attachment").
 */
const DetailsDialog: React.FC<DetailsDialogProps> = ({
  open,
  reasonLabel,
  reason,
  attachments,
  isDownloading,
  onView,
  onDownload,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <div data-testid="details-dialog">
      <SideDrawer
        isOpen={open}
        title={t("bay-hire.details-dialog-title")}
        onClose={onClose}
        onSubmit={onClose}
        submitLabel={t("common.ok", { defaultValue: "OK" })}
        hideCancelButton
        // DFT-0083: without an explicit width, SimpleSideDrawer falls back to `width:
        // fit-content`, which sizes itself off this drawer's own content's intrinsic
        // ("max-content") width. A long reason's own unwrapped width feeds straight into that
        // calculation - for a ~1000-char reason that's several thousand pixels, clamped only by
        // `maxWidth: 100vw`, i.e. exactly the "drawer expands to fill the screen" defect
        // (live-verified via DevTools on the deployed reproduction: `width: 1512px` = viewport
        // width). Fixed here to this drawer only, not a change to SimpleSideDrawer's shared
        // default (used by ~140 other call sites).
        width={400}
      >
        <div className="flex flex-col gap-2 px-6 py-2">
          <h4 className="text-sm font-semibold text-gray-900" data-testid="details-reason-label">
            {reasonLabel}
          </h4>
          <p className="text-sm text-gray-700 break-words" data-testid="details-reason">
            {reason || t("bay-hire.no-reason-provided")}
          </p>

          {attachments.length > 0 && (
            <BayHireAttachmentsSection
              attachments={attachments}
              isDownloading={isDownloading}
              onView={onView}
              onDownload={onDownload}
            />
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default DetailsDialog;
